import { DecimalPipe, KeyValue, KeyValuePipe } from '@angular/common'
import { HttpClient } from '@angular/common/http'
import { ChangeDetectorRef, Component, NgIterable, OnInit, WritableSignal, signal } from '@angular/core'
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms'
import { GoogleMapsModule } from '@angular/google-maps'
import { MatButtonModule } from '@angular/material/button'
import { MatInputModule } from '@angular/material/input'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'
import { FieldType, FieldTypeConfig } from '@ngx-formly/core'
import { Observable, of, Subscription } from 'rxjs'
import { catchError, map } from 'rxjs/operators'
import { FacilityService } from 'src/app/services/facility.service'
import { OrderService } from 'src/app/services/order.service'
import { FilterPipe } from 'src/pipes/filter.pipe/filter.pipe'
import { PhonePipe } from 'src/pipes/phone.pipe/phone.pipe'
import { Facility } from 'src/types/wizard'

@Component({
	selector: 'gwc-facility-finder',
	templateUrl: './facility.finder.component.html',
	styleUrls: ['./facility.finder.component.scss'],
	imports: [
		ReactiveFormsModule,
		MatInputModule,
		MatButtonModule,
		MatProgressSpinnerModule,
		PhonePipe,
		DecimalPipe,
		KeyValuePipe,
		FilterPipe,
		GoogleMapsModule
	]
})

export class FacilityFinderComponent extends FieldType<FieldTypeConfig> implements OnInit {
	protected readonly domain!: string
	protected readonly companies: { [key: string]: string } = {
		fedex: 'FedEx Office',
		cvs: 'CVS Pharmacy',
		walgreens: 'Walgreens',
		aaa: 'AAA'
	}
	protected readonly weekdays: { [key: string]: string } = {
		m: 'Mon',
		t: 'Tue',
		w: 'Wed',
		r: 'Thu',
		f: 'Fri',
		s: 'Sat',
		su: 'Sun',
	}
	protected readonly mapOptions: google.maps.MapOptions = {
		mapTypeControl: false,
		streetViewControl: false,
		styles: [
			{
				stylers: [
					{ gamma: 1 }
				]
			},
			{
				elementType: 'labels.text.stroke',
				stylers: [
					{ visibility: 'off' }
				]
			},
			{
				featureType: 'poi.business',
				elementType: 'labels.text',
				stylers: [
					{ visibility: 'off' }
				]
			},
			{
				featureType: 'poi.business',
				elementType: 'labels.icon',
				stylers: [
					{ visibility: 'off' }
				]
			},
			{
				featureType: 'poi.place_of_worship',
				elementType: 'labels.text',
				stylers: [
					{ visibility: 'off' }
				]
			},
			{
				featureType: 'poi.place_of_worship',
				elementType: 'labels.icon',
				stylers: [
					{ visibility: 'off' }
				]
			},
			{
				featureType: 'road',
				elementType: 'geometry',
				stylers: [
					{ visibility: 'simplified' }
				]
			},
			{
				featureType: 'water',
				stylers: [
					{ visibility: 'on' },
					{ saturation: 50 },
					{ gamma: 0 },
					{ hue: '#50a5d1' }
				]
			},
			{
				featureType: 'administrative.neighborhood',
				elementType: 'labels.text.fill',
				stylers: [
					{ color: '#333333' }
				]
			},
			{
				featureType: 'road.local',
				elementType: 'labels.text',
				stylers: [
					{ weight: 0.5 },
					{ color: '#333333' }
				]
			},
			{
				featureType: 'transit.station',
				elementType: 'labels.icon',
				stylers: [
					{ gamma: 1 },
					{ saturation: 50 }
				]
			}
		]
	}
	public facilities: WritableSignal<Facility[]> = signal([])
	public loadingFacilities: WritableSignal<boolean> = signal(false)
	public zipCodeControl = new FormControl('', [Validators.minLength(5), Validators.maxLength(5), Validators.pattern(/^\d{5}$/)])
	public selected: Facility | undefined
	public originalOrder = (a: KeyValue<string, string>, b: KeyValue<string, string>): number => {
		return 0
	}
	public apiLoaded: Observable<boolean> = of(false)
	public mapCenter!: google.maps.LatLngLiteral
	private nextPageSubscription!: Subscription

	constructor(
		private facilityService: FacilityService,
		private ref: ChangeDetectorRef,
		orderService: OrderService,
		httpClient: HttpClient,
	) {
		super()
		this.apiLoaded = httpClient.jsonp('https://maps.googleapis.com/maps/api/js?key=AIzaSyDrBuO_eyjOF8o1vZ2bdbOsc3xW6_ALheo', 'callback')
			.pipe(
				map(() => true),
				catchError(() => of(false)),
			)
		this.domain = orderService.active_domain
	}

	ngOnInit(): void {
		this.prefillZipCode()
		if (this.formControl.value && (this.formControl.value.facility || this.formControl.value.facility === null)) {
			this.formControl.patchValue(this.formControl.value.facility)
		}
	}

	private prefillZipCode() {
		if (this.formControl.value) {
			this.zipCodeControl.patchValue(this.formControl.value.zip)
		} else {
			if (this.model.address?.zip) {
				this.zipCodeControl.patchValue(this.model.address.zip)
			}
		}

		this.getFacilities()
	}

	public getFacilities(): any {
		let zip_code = this.zipCodeControl.getRawValue()

		if (zip_code) {
			let brand = ['fedex', 'aaa'].includes(this.domain) && this.props['category'] === 'passport-photo' ? this.domain : null

			if (this.domain === 'idp') {
				brand = 'aaa'
			}

			this.loadingFacilities.set(true)
			return this.facilityService.getFacilities(zip_code, this.props['category'], brand)
				.subscribe(response => {
					this.facilities.set(response)

					if (this.facilities()[0] && (!this.formControl.value || !this.formControl.value.address)) {
						this.formControl.patchValue(this.facilities()[0])
						this.mapCenter = {
							lat: this.facilities()[0].latitude,
							lng: this.facilities()[0].longitude
						}
					}

					this.loadingFacilities.set(false)
				})
		}
	}

	public selectFacility(facility: Facility): void {
		this.formControl.patchValue(facility)
		this.mapCenter = {
			lat: facility.latitude,
			lng: facility.longitude
		}
	}

	public hoursAvailable = (day: any): boolean => {
		return this.formControl.value.hours[day.key]
	}
}
